import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import { Product } from '../../types';
import { productService } from '../../services/productService';
import { purchaseItemService, PurchaseItemDTO } from '../../services/purchaseItemService';
import { salesItemService, SalesItemDTO } from '../../services/salesItemService';
import './ProductCategoryDeletePage.css';
import { ArrowLeft, Trash2 } from 'lucide-react';

import { getServerErrorMessage } from '../../utils/errorUtils';
const ProductDeletePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [purchaseItems, setPurchaseItems] = useState<PurchaseItemDTO[]>([]);
  const [salesItems, setSalesItems] = useState<SalesItemDTO[]>([]);
  const [canDelete, setCanDelete] = useState(true);

  useEffect(() => {
    loadProduct();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadProduct = async () => {
    setLoading(true);
    setError(null);
    try {
      const productId = parseInt(id || '0');
      const [productRes, purchaseRes, salesRes] = await Promise.all([
        productService.getProductById(productId),
        purchaseItemService.getPurchaseItemsByProduct(productId).catch(() => ({ data: { data: [] } })),
        salesItemService.getSalesItemsByProduct(productId).catch(() => ({ data: { data: [] } })),
      ]);

      if (productRes.data.data) {
        setProduct(productRes.data.data as Product);
      }

      const purchaseItemsData = purchaseRes?.data?.data || [];
      const salesItemsData = salesRes?.data?.data || [];
      setPurchaseItems(purchaseItemsData);
      setSalesItems(salesItemsData);

      setCanDelete(purchaseItemsData.length === 0 && salesItemsData.length === 0);
      setLoading(false);
    } catch {
      setError(t('product.failedToLoadProduct'));
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!canDelete) {
      setError('Cannot delete product with linked Purchase Items or Sales Items');
      return;
    }

    setDeleting(true);
    setError(null);
    try {
      await productService.deleteProduct(parseInt(id || '0'));
      navigate('/products', { state: { message: t('product.productDeletedSuccess') } });
    } catch (err: any) {
      setError(getServerErrorMessage(err) || t('product.failedToDeleteProduct'));
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="delete-loading-container">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </Layout>
    );
  }

  if (!product) {
    return (
      <Layout>
        <div className="delete-container">
          <div className="alert alert-danger">{error || t('product.productNotFound')}</div>
          <button className="btn btn-secondary" onClick={() => navigate('/products')}>
            {t('product.backToProducts')}
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="delete-container">
        {/* Navigation Breadcrumb */}
        <nav className="delete-breadcrumb">
          <button
            className="delete-breadcrumb-btn"
            onClick={() => navigate(`/products/${id}`)}
            title={t('product.backToProduct')}
          >
            <ArrowLeft size={16} strokeWidth={2} />
            {t('product.backToProduct')}
          </button>
        </nav>

        {/* Page Content */}
        <div className="delete-content">
          {/* Main Delete Card */}
          <div className="delete-card delete-card-main">
            <div className="delete-card-header delete-card-header-warning">
              <Trash2 size={24} strokeWidth={2} className="delete-icon" />
              <h2 className="delete-card-title">{t('product.deleteTitle')}</h2>
            </div>

            {/* Error Alert */}
            {error && (
              <div className="alert alert-danger alert-dismissible fade show">
                {error}
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setError(null)}
                  aria-label={t('common.close')}
                />
              </div>
            )}

            <div className="delete-card-body">
              <div className="delete-warning-section">
                <p className="delete-warning-text">
                  {t('product.deleteConfirmationMessage')}
                </p>
              </div>

              {/* Product Details */}
              <div className="delete-category-info">
                <div className="delete-info-item">
                  <label className="delete-info-label">{t('product.sku')}</label>
                  <p className="delete-info-value delete-code">{product.sku}</p>
                </div>

                <div className="delete-info-item">
                  <label className="delete-info-label">{t('product.productName')}</label>
                  <p className="delete-info-value">{product.name}</p>
                </div>

                <div className="delete-info-item">
                  <label className="delete-info-label">{t('product.category')}</label>
                  <p className="delete-info-value">{product.categoryName || 'N/A'}</p>
                </div>

                <div className="delete-info-item">
                  <label className="delete-info-label">{t('product.unitOfMeasure')}</label>
                  <p className="delete-info-value">{product.unitOfMeasure}</p>
                </div>
              </div>

              {/* Actions */}
              <div className="delete-actions">
                <button
                  className="btn-cancel"
                  onClick={() => navigate(`/products/${id}`)}
                  disabled={deleting}
                  title={t('common.cancel')}
                >
                  {t('common.cancel')}
                </button>

                <button
                  className="btn-delete"
                  onClick={handleDelete}
                  disabled={deleting || !canDelete}
                  title={!canDelete ? 'Cannot delete product with linked items' : ''}
                >
                  {deleting ? (
                    <>
                      <span className="btn-spinner"></span>
                      {t('common.deleting')}
                    </>
                  ) : (
                    <>
                      <Trash2 size={16} strokeWidth={2} />
                      {t('product.deleteProduct')}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          <div className="delete-sidebar">
            {!canDelete && (
              <div className="delete-card" style={{ borderLeft: '4px solid #DC143C' }}>
                <div className="delete-card-header" style={{ backgroundColor: '#fee2e2' }}>
                  <h3 className="delete-card-title" style={{ color: '#991b1b' }}>
                    Cannot Delete Product
                  </h3>
                </div>
                <div className="delete-card-body">
                  {purchaseItems.length > 0 && (
                    <div className="delete-warning-item">
                      <strong>
                        Linked Purchase Items ({purchaseItems.length})
                      </strong>
                      <p style={{ fontSize: '12px', marginTop: '6px' }}>
                        This product has purchase items linked to it. Remove all purchase items before deleting.
                      </p>
                      <ul style={{ fontSize: '12px', marginTop: '6px', paddingLeft: '16px' }}>
                        {purchaseItems.slice(0, 3).map((item) => (
                          <li key={item.purchaseItemId}>
                            {item.supplierName} - {item.supplierItemCode}
                          </li>
                        ))}
                        {purchaseItems.length > 3 && <li>... and {purchaseItems.length - 3} more</li>}
                      </ul>
                    </div>
                  )}
                  {salesItems.length > 0 && (
                    <div className="delete-warning-item">
                      <strong>
                        Linked Sales Items ({salesItems.length})
                      </strong>
                      <p style={{ fontSize: '12px', marginTop: '6px' }}>
                        This product has sales items linked to it. Remove all sales items before deleting.
                      </p>
                      <ul style={{ fontSize: '12px', marginTop: '6px', paddingLeft: '16px' }}>
                        {salesItems.slice(0, 3).map((item) => (
                          <li key={item.salesItemId}>
                            {item.salesChannel} - ${Number(item.sellingPrice).toFixed(2)} {item.currency}
                          </li>
                        ))}
                        {salesItems.length > 3 && <li>... and {salesItems.length - 3} more</li>}
                      </ul>
                    </div>
                  )}
                  <button
                    type="button"
                    className="detail-quick-link"
                    onClick={() => navigate('/purchase-items')}
                    style={{ marginTop: '12px', width: '100%', textAlign: 'left' }}
                  >
                    View Purchase Items
                  </button>
                  <button
                    type="button"
                    className="detail-quick-link"
                    onClick={() => navigate('/sales-items')}
                    style={{ marginTop: '6px', width: '100%', textAlign: 'left' }}
                  >
                    View Sales Items
                  </button>
                </div>
              </div>
            )}

            <div className="delete-card">
              <div className="delete-card-header">
                <h3 className="delete-card-title">{t('product.safetyInformation')}</h3>
              </div>
              <div className="delete-card-body">
                <div className="delete-help-section">
                  <div className="delete-help-item">
                    <strong className="delete-help-strong">{t('product.permanentAction')}</strong>
                    <p>{t('product.deleteWarning')}</p>
                  </div>
                  <div className="delete-help-item">
                    <strong className="delete-help-strong">{t('product.relatedData')}</strong>
                    <p>{t('product.deleteRelatedDataWarning')}</p>
                  </div>
                  <div className="delete-help-item">
                    <strong className="delete-help-strong">{t('product.confirmationRequired')}</strong>
                    <p>{t('product.deleteConfirmationRequired')}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ProductDeletePage;
